(() => {
  'use strict';
  const VERSION = 'welcome_offer_v3_phone_fix_20261001';
  const STORAGE_KEY = 'ingenio.welcome-offer.v2';
  const PENDING_KEY = 'ingenio.welcome-offer.pending';
  const EVENTS = new Set(['welcome_banner_view', 'welcome_banner_click', 'welcome_banner_dismiss', 'welcome_popup_view', 'welcome_popup_dismiss', 'welcome_offer_submit', 'welcome_email_submit', 'welcome_whatsapp_opt_in', 'welcome_registration_success', 'welcome_registration_error', 'welcome_code_applied', 'welcome_validation_error', 'welcome_qa_probe']);
  const normalizePhone = (value, country) => {
    const raw = String(value || '').trim();
    const code = String(country || '').replace(/^\+/, '');
    if (!/^\d{1,3}$/.test(code) || !raw || /[^\d+()\s.-]/.test(raw)) return '';
    let digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+')) {
      if (!digits.startsWith(code)) return '';
      digits = digits.slice(code.length);
    } else if (code === '57' && digits.length === 12 && digits.startsWith('57')) {
      digits = digits.slice(2);
    }
    if (code === '57' && !/^3\d{9}$/.test(digits)) return '';
    if (code === '1' && !/^[2-9]\d{9}$/.test(digits)) return '';
    if (/^(\d)\1+$/.test(digits)) return '';
    const full = code + digits;
    return /^[1-9]\d{7,14}$/.test(full) ? '+' + full : '';
  };
  const requestTags = (phone, emailConsent, whatsappConsent) => {
    if (!phone || !emailConsent || !whatsappConsent) return 'newsletter';
    return ['newsletter', 'welcome-offer-request-v2', 'welcome-offer-email-consent', 'welcome-offer-whatsapp-consent', 'welcome-offer-wa-' + phone.replace(/\D/g, '')].join(', ');
  };
  const safeProperties = (fields) => {
    const clean = {};
    for (const key of ['email_opt_in', 'whatsapp_opt_in']) if (typeof fields[key] === 'boolean') clean[key] = fields[key];
    for (const key of ['surface', 'error_field', 'delivery_channel']) if (/^[a-z_]{1,32}$/.test(fields[key] || '')) clean[key] = fields[key];
    return clean;
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { normalizePhone, requestTags, safeProperties };
  if (typeof window === 'undefined' || typeof HTMLElement === 'undefined') return;

  class IngenioWelcomeOffer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.controller = new AbortController();
      this.on = (target, event, callback, capture = false) => target?.addEventListener(event, callback, { signal: this.controller.signal, capture });
      this.preview = this.dataset.previewMode === 'true';
      this.params = new URLSearchParams(location.search);
      this.dialog = this.querySelector('[data-dialog]');
      this.banner = this.querySelector('[data-banner]');
      this.form = this.querySelector('form');
      this.formPanel = this.querySelector('[data-form-panel]');
      this.success = this.querySelector('[data-success]');
      this.email = this.querySelector('[data-email]');
      this.phone = this.querySelector('[data-phone]');
      this.country = this.querySelector('[data-country]');
      this.emailConsent = this.querySelector('[data-email-consent]');
      this.whatsappConsent = this.querySelector('[data-whatsapp-consent]');
      this.submit = this.querySelector('[data-submit]');
      this.error = this.querySelector('[data-error]');
      this.review = this.querySelector('[data-review]');
      this.queue = [];
      this.setVariant(this.preview ? this.params.get('welcome_variant') || this.dataset.variant : this.dataset.variant);
      this.submit.disabled = false;
      this.on(this.form, 'submit', event => this.handleSubmit(event), true);
      this.on(this.form, 'input', () => this.clearError());
      this.on(this.country, 'change', () => { this.clearError(); this.querySelector('[data-phone-help]').hidden = this.country.value !== '57'; });
      this.querySelectorAll('[data-modal-close]').forEach(el => this.on(el, 'click', () => this.closeModal()));
      this.on(this.dialog, 'cancel', event => { event.preventDefault(); this.closeModal(); });
      this.on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeModal();
      });
      this.on(this.querySelector('[data-banner-close]'), 'click', () => { this.banner.hidden = true; document.body.classList.remove('iw-invitation-visible'); this.save({ status: 'dismissed', dismissedAt: new Date().toISOString() }); this.track('welcome_banner_dismiss', { surface: 'banner' }); });
      this.on(this.querySelector('[data-open-form]'), 'click', () => { this.track('welcome_banner_click', { surface: 'banner' }); this.openModal(); });
      this.querySelectorAll('[data-variant-choice]').forEach(el => this.on(el, 'click', () => {
        this.resetPreview(); this.setVariant(el.dataset.variantChoice);
        const url = new URL(location.href); url.searchParams.set('welcome_variant', this.variant); history.replaceState({}, '', url); this.showOffer();
      }));
      this.on(this.querySelector('[data-reopen]'), 'click', () => { this.resetPreview(); this.showOffer(); });
      if (this.review) this.review.hidden = false;
      const qa = this.querySelector('[data-qa]');
      if (qa) qa.hidden = this.params.get('welcome_measurement_test') !== '1';
      this.on(this.querySelector('[data-qa-send]'), 'click', () => this.track('welcome_qa_probe', { surface: 'draft' }, true));
      this.on(document, 'visitorConsentCollected', () => this.flush());
      this.on(document, 'ingenio:analytics-ready', () => this.flush());
      if (!window.Shopify?.customerPrivacy) window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => this.flush());
      this.recordApplied();
      if (this.querySelector('[data-registration-success]') && this.hasPendingRequest()) { this.clearPendingRequest(); this.save({ status: 'submitted', submittedAt: new Date().toISOString() }); this.openModal(true); this.track('welcome_registration_success'); return; }
      if (this.querySelector('[data-server-error]')) { this.openModal(); this.track('welcome_registration_error', { error_field: 'server' }); return; }
      if (this.shouldShow()) this.timer = setTimeout(() => this.showOffer(), this.preview ? 250 : Number(this.dataset.delaySeconds || 8) * 1000);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); if (this.dialog?.open) this.closeModal(false); }
    setVariant(value) {
      this.variant = ['1', '2', '5'].includes(value) ? value : '2';
      this.dataset.variant = this.variant;
      this.querySelector('[data-ticket]').hidden = this.variant !== '5';
      this.querySelector('[data-submit-normal]').hidden = this.variant === '5';
      this.querySelector('[data-submit-ticket]').hidden = this.variant !== '5';
      this.querySelectorAll('[data-variant-choice]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.variantChoice === this.variant)));
    }
    read() { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; } }
    save(next) { if (this.preview) return; try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...this.read(), ...next, version: 2 })); } catch {} }
    shouldShow() {
      if (this.preview) return true;
      const state = this.read();
      if (['submitted', 'applied', 'purchased'].includes(state.status) || state.submittedAt) return false;
      const dismissed = Date.parse(state.dismissedAt || '');
      return !Number.isFinite(dismissed) || Date.now() - dismissed >= Number(this.dataset.dismissDays || 15) * 86400000;
    }
    showOffer() {
      clearTimeout(this.timer);
      if (this.variant === '2') { this.banner.hidden = false; document.body.classList.add('iw-invitation-visible'); this.track('welcome_banner_view', { surface: 'banner' }); }
      else this.openModal();
    }
    openModal(success = false) {
      this.banner.hidden = true;
      document.body.classList.remove('iw-invitation-visible');
      this.formPanel.hidden = success; this.success.hidden = !success;
      if (!this.dialog.open) {
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        if (this.review) this.dialog.append(this.review);
        this.dialog.showModal();
      }
      if (success) this.success.focus({ preventScroll: true });
      else { this.dialog.focus({ preventScroll: true }); this.track('welcome_popup_view', { surface: 'form' }); }
    }
    closeModal(record = true) {
      if (!this.dialog.open) return;
      if (record && this.success.hidden) { this.track('welcome_popup_dismiss', { surface: 'form' }); this.save({ status: 'dismissed', dismissedAt: new Date().toISOString() }); }
      this.dialog.close();
      document.documentElement.style.overflow = this.previousOverflow || '';
      if (this.review) this.prepend(this.review);
    }
    resetPreview() { this.closeModal(false); this.banner.hidden = true; document.body.classList.remove('iw-invitation-visible'); this.formPanel.hidden = false; this.success.hidden = true; this.form.reset(); this.querySelector('[data-phone-help]').hidden = this.country.value !== '57'; this.clearError(); }
    clearError() { this.error.hidden = true; [this.email, this.phone].forEach(el => el.removeAttribute('aria-invalid')); }
    showError(key, element) {
      this.error.textContent = this.dataset['error' + key[0].toUpperCase() + key.slice(1)]; this.error.hidden = false;
      if (['email', 'phone'].includes(key)) element.setAttribute('aria-invalid', 'true');
      element.focus(); this.track('welcome_validation_error', { error_field: key });
    }
    handleSubmit(event) {
      if (this.preview) { event.preventDefault(); event.stopImmediatePropagation(); }
      this.clearError();
      const tags = this.querySelector('[data-tags]');
      const marketing = this.querySelector('[data-marketing]');
      if (tags) tags.value = 'newsletter';
      if (marketing) marketing.value = 'false';
      if (!this.email.validity.valid) { this.rejectSubmission(event); this.showError('email', this.email); return; }
      const phone = normalizePhone(this.phone.value, this.country.value);
      if (!phone) { this.rejectSubmission(event); this.showError('phone', this.phone); return; }
      if (!this.emailConsent.checked || !this.whatsappConsent.checked) { this.rejectSubmission(event); this.showError('consent', !this.emailConsent.checked ? this.emailConsent : this.whatsappConsent); return; }
      this.track('welcome_offer_submit', { email_opt_in: true, whatsapp_opt_in: true });
      this.track('welcome_email_submit', { whatsapp_opt_in: true });
      this.track('welcome_whatsapp_opt_in');
      if (this.preview) { this.openModal(true); this.track('welcome_registration_success'); return; }
      this.phone.value = phone;
      tags.value = requestTags(phone, true, true); marketing.value = 'true';
      const requestedAt = new Date().toISOString();
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ formId: this.form.id, requestedAt })); } catch {}
      this.save({ status: 'pending', submittedAt: null, requestedAt });
    }
    rejectSubmission(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.clearPendingRequest();
    }
    hasPendingRequest() {
      try {
        const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || '{}');
        const age = Date.now() - Date.parse(pending.requestedAt || '');
        return pending.formId === this.form.id && age >= 0 && age < 30 * 60 * 1000 && location.hash === '#' + this.form.id;
      } catch { return false; }
    }
    clearPendingRequest() { try { sessionStorage.removeItem(PENDING_KEY); } catch {} }
    recordApplied() {
      if (this.preview || this.params.get('welcome_offer') !== 'applied') return;
      const rawChannel = this.params.get('utm_medium');
      this.track('welcome_code_applied', { delivery_channel: ['email', 'whatsapp'].includes(rawChannel) ? rawChannel : 'unknown' });
      this.save({ status: 'applied', appliedAt: new Date().toISOString() });
      const url = new URL(location.href); url.searchParams.delete('welcome_offer'); history.replaceState({}, '', url);
    }
    log(name, status) {
      const log = this.querySelector('[data-qa-log]');
      if (!log) return;
      const row = document.createElement('li'); row.textContent = name + ' · ' + status; log.append(row);
      while (log.children.length > 24) log.firstElementChild.remove();
    }
    track(name, fields = {}, qaSend = false) {
      if (!EVENTS.has(name)) return;
      const payload = { welcome_offer_version: VERSION, popup_variant: 'option_' + this.variant, source: 'theme_popup', ...safeProperties(fields) };
      if (this.preview && !qaSend) { this.log(name.replace('welcome_', 'welcome_qa_'), 'simulado'); return; }
      if (!window.Shopify?.customerPrivacy) { this.queue.push({ name, payload, qaSend }); this.log(name, 'pendiente API de privacidad'); return; }
      this.send(name, payload, qaSend);
    }
    send(name, payload, qaSend) {
      if (!window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) { this.log(name, 'sin permiso de analítica'); return; }
      if (!/^G-[A-Z0-9]+$/.test(this.dataset.ga4 || '')) { this.log(name, 'destino no configurado'); return; }
      window.dataLayer = window.dataLayer || [];
      const params = { send_to: this.dataset.ga4, ...payload, ...(qaSend ? { debug_mode: true } : {}) };
      (function () { window.dataLayer.push(arguments); })('event', name, params);
      document.dispatchEvent(new CustomEvent('ingenio:welcome-offer', { detail: { event: name, ...payload } }));
      this.log(name, 'encolado para GA4');
    }
    flush() { if (!window.Shopify?.customerPrivacy) return; const pending = this.queue.splice(0); pending.forEach(item => this.send(item.name, item.payload, item.qaSend)); }
  }
  if (!customElements.get('ingenio-welcome-offer')) customElements.define('ingenio-welcome-offer', IngenioWelcomeOffer);
})();
